import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Pagination,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "./InlineFormError";
import { InventoryOpsActivityLogStore } from "../../../../stores/ProjectManagement/InventoryOpsActivityLogStore";
import {
  InventoryOpsActivityAction,
  InventoryOpsActivityEntityType,
  InventoryOpsActivityLog,
} from "../../../../../../models/ProjectManagement/InventoryOpsActivityLog";

const ENTITY_LABELS: Record<InventoryOpsActivityEntityType, string> = {
  RAW_INWARD: "Raw Inward",
  RAW_INWARD_QC: "1st QC",
  PRINT_OUTWARD: "Print Outward",
  PRINT_INWARD: "Print Inward",
  PRINT_INWARD_QC: "2nd QC",
};

const ACTION_VERBS: Record<InventoryOpsActivityAction, string> = {
  RECEIVED: "updated received status on",
  QTY: "updated quantity on",
  QC: "recorded",
  CANCEL: "cancelled",
  CREATE: "created",
  UNDO_LAST: "undid the last batch on",
  REVERT: "reverted",
};

const PRINT_ENTITY_TYPES: InventoryOpsActivityEntityType[] = [
  "PRINT_OUTWARD",
  "PRINT_INWARD",
  "PRINT_INWARD_QC",
];

const PLATFORM_LABELS: Record<string, string> = {
  web: "Web",
  ios: "iOS",
  android: "Android",
  other: "Other",
};

const formatLogDate = (value: string) => {
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY, hh:mm A") : "";
};

/** Human-readable lines for the request payload (not the server result). */
const getPayloadDetails = (log: InventoryOpsActivityLog): string[] => {
  const payload = log.payload;
  if (!payload) return [];
  const isQc =
    log.entity_type === "RAW_INWARD_QC" ||
    log.entity_type === "PRINT_INWARD_QC";
  const details: string[] = [];

  if (log.action === "UNDO_LAST" || log.action === "REVERT") {
    const removed = payload.removed;
    if (removed) {
      const qtyWord = log.entity_type === "PRINT_OUTWARD" ? "sent" : "received";
      details.push(`Removed ${removed.quantity ?? 0} ${qtyWord}`);
      if ((removed.rejected_quantity ?? 0) > 0) {
        details.push(`Removed ${removed.rejected_quantity} rejected`);
      }
      if ((removed.passed_quantity ?? 0) > 0) {
        details.push(`Removed ${removed.passed_quantity} passed`);
      }
      (removed.sku_variants || []).forEach((line) => {
        if ((line.quantity ?? 0) > 0) {
          details.push(`${line.title || line.sku}: −${line.quantity}`);
        }
      });
    }
    const movementIds = payload.movement_ids || [];
    if (movementIds.length > 0) {
      details.push(
        `Batch${movementIds.length === 1 ? "" : "es"} ${movementIds
          .map((id) => `#${id}`)
          .join(", ")}`,
      );
    }
    return details;
  }

  if (payload.seeded_from) {
    details.push("Created automatically by 1st QC");
  }
  if (payload.counted_at_qc) {
    details.push("Counted at QC");
  }
  if (payload.mark_received !== undefined) {
    details.push(
      payload.mark_received ? "Marked received" : "Marked not received",
    );
  }
  if (payload.all_qty) {
    details.push(isQc ? "Rejected all remaining" : "All remaining");
  }

  const skuLines = payload.sku_variants || [];
  if (skuLines.length > 0) {
    skuLines.forEach((line) => {
      const name = line.title || line.sku;
      if (isQc) {
        const parts: string[] = [];
        if ((line.tested_quantity ?? 0) > 0) {
          parts.push(`tested ${line.tested_quantity}`);
        } else if ((line.passed_quantity ?? 0) > 0) {
          parts.push(`passed ${line.passed_quantity}`);
        }
        if ((line.quantity ?? 0) > 0 || parts.length === 0) {
          parts.push(`rejected ${line.quantity ?? 0}`);
        }
        details.push(`${name}: ${parts.join(", ")}`);
      } else {
        details.push(`${name}: ${line.quantity ?? 0}`);
      }
    });
  } else if (payload.quantity !== undefined && !payload.all_qty) {
    details.push(
      isQc ? `Rejected ${payload.quantity}` : `Qty ${payload.quantity}`,
    );
  }

  if ((payload.tested_quantity ?? 0) > 0) {
    details.push(`Tested ${payload.tested_quantity}`);
  } else if ((payload.passed_quantity ?? 0) > 0) {
    details.push(`Passed ${payload.passed_quantity}`);
  }
  if (payload.pass_remaining) {
    details.push("Passed all remaining");
  }
  if (payload.printer_id) {
    details.push(`Printer #${payload.printer_id}`);
  }
  if (payload.print_outward_timeline) {
    const due = dayjs(payload.print_outward_timeline);
    if (due.isValid()) details.push(`Due ${due.format("DD MMM YYYY")}`);
  }
  if (payload.warehouse_id) {
    details.push(`Warehouse ${payload.warehouse_id}`);
  }

  return details;
};

interface InventoryOpsActivityDrawerProps {
  open: boolean;
  onClose: () => void;
  store: InventoryOpsActivityLogStore;
  /** Hide print stage filters for non-customisable products. */
  showPrintStages: boolean;
  /** Reloads the main screen behind the drawer. */
  onRefreshMain: () => Promise<void>;
  isMainRefreshing?: boolean;
}

const InventoryOpsActivityDrawer: React.FC<InventoryOpsActivityDrawerProps> = ({
  open,
  onClose,
  store,
  showPrintStages,
  onRefreshMain,
  isMainRefreshing,
}) => {
  const theme = useTheme();
  const isRefreshing = store.isLoading || Boolean(isMainRefreshing);

  const filterOptions: Array<{
    value: InventoryOpsActivityEntityType | null;
    label: string;
  }> = [
    { value: null, label: "All" },
    ...(Object.keys(ENTITY_LABELS) as InventoryOpsActivityEntityType[])
      .filter((type) => showPrintStages || !PRINT_ENTITY_TYPES.includes(type))
      .map((type) => ({ value: type, label: ENTITY_LABELS[type] })),
  ];

  const handleRefresh = () => {
    Promise.all([store.refreshData(), onRefreshMain()]).catch(() => undefined);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          height: "80vh",
          borderRadius: "10px 0 0 10px",
          boxShadow: 3,
          display: "flex",
          flexDirection: "column",
          mt: "13vh",
          width: {
            xl: "30vw",
            lg: "40vw",
            md: "50vw",
            sm: "60vw",
            xs: "85vw",
          },
        },
      }}
    >
      <Box sx={{ pt: 2, px: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <CustomTypography variant="heading4">Activity</CustomTypography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={handleRefresh}
              disabled={isRefreshing}
              size="small"
              aria-label="Refresh"
              title="Refresh activity and screen"
            >
              {isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <CustomTypography
          variant="heading8"
          color={theme.palette.customColors.greyTextColor}
        >
          PO {store.poId ?? "-"} · {store.totalCount} action
          {store.totalCount === 1 ? "" : "s"}
        </CustomTypography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
          {filterOptions.map((option) => {
            const selected = store.entityType === option.value;
            return (
              <Chip
                key={option.label}
                label={option.label}
                size="small"
                clickable
                onClick={() => {
                  store.setEntityType(option.value);
                }}
                sx={{
                  fontSize: "12px",
                  fontWeight: selected ? 600 : 400,
                  color: selected
                    ? theme.palette.customColors.white
                    : undefined,
                  backgroundColor: selected
                    ? theme.palette.customColors.purple
                    : undefined,
                  "&:hover": selected
                    ? { backgroundColor: theme.palette.customColors.purple }
                    : undefined,
                }}
              />
            );
          })}
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        <InlineFormError message={store.error ?? undefined} />

        {store.isLoading && store.logs.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : store.logs.length === 0 && !store.error ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <CustomTypography variant="heading6">
              No activity recorded yet
            </CustomTypography>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.greyTextColor}
            >
              Actions from before activity logging started are in Summary.
            </CustomTypography>
          </Box>
        ) : (
          store.logs.map((log, index) => {
            const details = getPayloadDetails(log);
            const reason =
              log.payload?.rejection_reason || log.payload?.reason;
            const images = log.payload?.qc_images || [];
            return (
              <Box key={log.id}>
                {index > 0 && <Divider sx={{ my: 1.5 }} />}
                <CustomTypography variant="heading8" fontWeight={400}>
                  <span>{log.admin_name || "Unknown admin"}</span>{" "}
                  <span
                    style={{ color: theme.palette.customColors.greyTextColor }}
                  >
                    {ACTION_VERBS[log.action] || log.action.toLowerCase()}
                  </span>{" "}
                  <span>
                    {ENTITY_LABELS[log.entity_type] || log.entity_type}
                  </span>
                  {log.entity_id ? (
                    <span
                      style={{
                        color: theme.palette.customColors.greyTextColor,
                      }}
                    >
                      {` #${log.entity_id}`}
                    </span>
                  ) : null}
                </CustomTypography>
                <CustomTypography
                  variant="heading9"
                  fontWeight={400}
                  color={theme.palette.customColors.greyTextColor}
                  style={{ marginTop: 2 }}
                >
                  {formatLogDate(log.created_date)}
                  {log.platform
                    ? ` · ${PLATFORM_LABELS[log.platform] || log.platform}`
                    : ""}
                </CustomTypography>

                {details.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}
                  >
                    {details.map((detail) => (
                      <Chip
                        key={detail}
                        label={detail}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "11px" }}
                      />
                    ))}
                  </Box>
                )}
                {reason && (
                  <CustomTypography
                    variant="heading9"
                    fontWeight={400}
                    color={theme.palette.customColors.greyTextColor}
                    style={{ marginTop: 6 }}
                  >
                    Reason: {reason}
                  </CustomTypography>
                )}
                {images.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
                  >
                    {images.map((url) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={url}
                          alt="QC"
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      </a>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {store.totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Pagination
            size="small"
            count={store.totalPages}
            page={store.pageNo}
            disabled={store.isLoading}
            onChange={(_, page) => {
              store.setPage(page);
            }}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default observer(InventoryOpsActivityDrawer);
